import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  FileText,
  Download,
  Shield,
  Lock,
  Server,
  Database,
  Users,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  BookOpen,
  Clock,
  Layers,
  Cpu,
  Globe
} from 'lucide-react'
import LandingHeader from '../components/LandingHeader'

// Table of Contents Item
function TOCItem({ number, title, description, icon: Icon }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-purple-200 hover:shadow-md transition-all">
      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-purple-600" />
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-purple-600 font-mono">{number}</span>
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
    </div>
  )
}

// Key Point Card
function KeyPoint({ icon: Icon, title, description, color = 'purple' }) {
  const colors = {
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    indigo: 'bg-indigo-100 text-indigo-600'
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
      <div className={`w-12 h-12 ${colors[color]} rounded-xl flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  )
}

export default function Whitepaper() {
  const { t, i18n } = useTranslation()

  const currentLang = i18n.language || 'en'
  const whitepaperUrl = currentLang === 'en' ? '/whitepaper.html' : `/whitepaper-${currentLang}.html`

  const handleDownloadPDF = () => {
    // Open the localized HTML version which can be printed/saved as PDF
    const printWindow = window.open(whitepaperUrl, '_blank')
    // Wait for the window to load, then trigger print dialog
    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print()
        }, 500)
      }
    }
  }

  const handleViewOnline = () => {
    // Open the localized HTML version for reading
    window.open(whitepaperUrl, '_blank')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingHeader />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <FileText className="w-4 h-4 text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">{t('whitepaper.badge')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('whitepaper.title')}
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto mb-4">
              {t('whitepaper.subtitle')}
            </p>
            <p className="text-purple-300 mb-8">
              {t('whitepaper.date')}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center gap-2 bg-white text-purple-900 px-6 py-3 rounded-xl font-semibold hover:bg-purple-50 transition-colors"
              >
                <Download className="w-5 h-5" />
                {t('whitepaper.downloadPdf')}
              </button>
              <button
                onClick={handleViewOnline}
                className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
              >
                <BookOpen className="w-5 h-5" />
                {t('whitepaper.readOnline')}
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {[
                { value: '12', label: t('whitepaper.stats.chapters') },
                { value: '128-bit', label: t('whitepaper.stats.security') },
                { value: '4', label: t('whitepaper.stats.mlModels') },
                { value: 'Post-Quantum', label: t('whitepaper.stats.safe') },
              ].map((stat, index) => (
                <div key={index} className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-purple-200">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Abstract Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              {t('whitepaper.abstract.title')}
            </h2>
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('whitepaper.abstract.p1start')}<strong>{t('whitepaper.abstract.p1platform')}</strong>{t('whitepaper.abstract.p1mid')}<strong>{t('whitepaper.abstract.p1tech')}</strong>{t('whitepaper.abstract.p1end')}
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('whitepaper.abstract.p2')}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {t('whitepaper.abstract.p3start')}<strong>{t('whitepaper.abstract.p3bold')}</strong>{t('whitepaper.abstract.p3end')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('whitepaper.toc.title')}</h2>
            <p className="text-gray-600">{t('whitepaper.toc.subtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <TOCItem number="01" title={t('whitepaper.toc.ch01.title')} description={t('whitepaper.toc.ch01.description')} icon={BookOpen} />
            <TOCItem number="02" title={t('whitepaper.toc.ch02.title')} description={t('whitepaper.toc.ch02.description')} icon={Shield} />
            <TOCItem number="03" title={t('whitepaper.toc.ch03.title')} description={t('whitepaper.toc.ch03.description')} icon={Lock} />
            <TOCItem number="04" title={t('whitepaper.toc.ch04.title')} description={t('whitepaper.toc.ch04.description')} icon={Layers} />
            <TOCItem number="05" title={t('whitepaper.toc.ch05.title')} description={t('whitepaper.toc.ch05.description')} icon={Cpu} />
            <TOCItem number="06" title={t('whitepaper.toc.ch06.title')} description={t('whitepaper.toc.ch06.description')} icon={Users} />
            <TOCItem number="07" title={t('whitepaper.toc.ch07.title')} description={t('whitepaper.toc.ch07.description')} icon={CheckCircle} />
            <TOCItem number="08" title={t('whitepaper.toc.ch08.title')} description={t('whitepaper.toc.ch08.description')} icon={Globe} />
            <TOCItem number="09" title={t('whitepaper.toc.ch09.title')} description={t('whitepaper.toc.ch09.description')} icon={Clock} />
            <TOCItem number="10" title={t('whitepaper.toc.ch10.title')} description={t('whitepaper.toc.ch10.description')} icon={ChevronRight} />
            <TOCItem number="11" title={t('whitepaper.toc.ch11.title')} description={t('whitepaper.toc.ch11.description')} icon={ArrowRight} />
            <TOCItem number="12" title={t('whitepaper.toc.ch12.title')} description={t('whitepaper.toc.ch12.description')} icon={FileText} />
          </div>
        </div>
      </section>

      {/* Key Points Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('whitepaper.keyInsights.title')}</h2>
            <p className="text-gray-600">{t('whitepaper.keyInsights.subtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KeyPoint
              icon={Lock}
              title={t('whitepaper.keyInsights.fhe.title')}
              description={t('whitepaper.keyInsights.fhe.description')}
              color="purple"
            />
            <KeyPoint
              icon={Database}
              title={t('whitepaper.keyInsights.zeroExposure.title')}
              description={t('whitepaper.keyInsights.zeroExposure.description')}
              color="blue"
            />
            <KeyPoint
              icon={Users}
              title={t('whitepaper.keyInsights.multiParty.title')}
              description={t('whitepaper.keyInsights.multiParty.description')}
              color="green"
            />
            <KeyPoint
              icon={Shield}
              title={t('whitepaper.keyInsights.compliance.title')}
              description={t('whitepaper.keyInsights.compliance.description')}
              color="indigo"
            />
          </div>
        </div>
      </section>

      {/* Technical Highlights */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-purple-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">{t('whitepaper.technicalHighlights.title')}</h2>
            <p className="text-purple-200">{t('whitepaper.technicalHighlights.subtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4">{t('whitepaper.technicalHighlights.stack')}</h3>
              <ul className="space-y-2 text-purple-100">
                <li>• Django 5.2 LTS Backend</li>
                <li>• TenSEAL (CKKS) Encryption</li>
                <li>• Arbitrum L2 Blockchain</li>
                <li>• React + Vite Dashboard</li>
                <li>• Python & TypeScript SDKs</li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4">{t('whitepaper.technicalHighlights.mlModels')}</h3>
              <ul className="space-y-2 text-purple-100">
                <li>• Linear Regression</li>
                <li>• Logistic Regression</li>
                <li>• Decision Trees</li>
                <li>• K-Means Clustering</li>
                <li>• Ensemble Methods</li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4">{t('whitepaper.technicalHighlights.security')}</h3>
              <ul className="space-y-2 text-purple-100">
                <li>• 128/192/256-bit levels</li>
                <li>• Post-quantum secure (LWE)</li>
                <li>• Commit-reveal voting</li>
                <li>• Blockchain audit trail</li>
                <li>• Zero-knowledge processing</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-12 border border-purple-100">
              <FileText className="w-16 h-16 text-purple-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('whitepaper.ctaSection.title')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto">
                {t('whitepaper.ctaSection.description')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={handleDownloadPDF}
                  className="inline-flex items-center gap-2 bg-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-purple-700 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  {t('whitepaper.downloadPdf')}
                </button>
                <button
                  onClick={handleViewOnline}
                  className="inline-flex items-center gap-2 border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-colors"
                >
                  <FileText className="w-5 h-5" />
                  {t('whitepaper.readOnline')}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                {t('whitepaper.ctaSection.version')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-400" />
              <span className="text-white font-bold text-xl">Xcapit Privacy</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="/" className="text-slate-400 hover:text-white transition-colors">
                {t('whitepaper.footerNav.home')}
              </a>
              <a href="/privacy-tech" className="text-slate-400 hover:text-white transition-colors">
                {t('whitepaper.footerNav.technicalDocs')}
              </a>
              <a href="/hub" className="text-slate-400 hover:text-white transition-colors">
                {t('whitepaper.footerNav.useCases')}
              </a>
              <a href="https://apifhe.xcapit.com/api/v2/docs/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                {t('whitepaper.footerNav.apiDocs')}
              </a>
            </div>
            <p className="text-slate-500 text-sm">
              {t('whitepaper.footerNav.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
